import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Camera,
  Plus,
  Trash2,
  Edit,
  Download,
  Share2,
  QrCode,
  MapPin,
  Phone,
  Mail,
  DollarSign,
  Wifi,
  Car,
  Archive,
  Shirt,
  DoorClosed,
  Users,
  Droplet,
  Sun,
  Thermometer,
  UserCheck,
  Coffee,
  Box,
  Clock,
  Check,
  X,
  ExternalLink,
  Image,
  Loader2,
  Copy
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";

const AMENITY_ICONS = {
  wifi: Wifi,
  parking: Car,
  storage: Archive,
  washer_dryer: Shirt,
  private_suite: DoorClosed,
  shared_space: Users,
  sink: Droplet,
  natural_light: Sun,
  ac: Thermometer,
  reception: UserCheck,
  break_room: Coffee,
  towel_service: Shirt,
  product_storage: Box,
  "24_7_access": Clock,
};

const STANDARD_AMENITIES = [
  { id: "wifi", name: "Free WiFi" },
  { id: "parking", name: "Parking Available" },
  { id: "storage", name: "Personal Storage" },
  { id: "washer_dryer", name: "Washer/Dryer" },
  { id: "private_suite", name: "Private Suite" },
  { id: "shared_space", name: "Shared Space" },
  { id: "sink", name: "Shampoo Bowl/Sink" },
  { id: "natural_light", name: "Natural Lighting" },
  { id: "ac", name: "Climate Control" },
  { id: "reception", name: "Reception Area" },
  { id: "break_room", name: "Break Room Access" },
  { id: "towel_service", name: "Towel Service" },
  { id: "product_storage", name: "Product Storage" },
  { id: "24_7_access", name: "24/7 Access" },
];

const BoothMarketingManager = ({ salonId, salonInfo }) => {
  const { token } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cardRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    booth_type: "standard",
    size_sqft: "",
    weekly_rate: "",
    monthly_rate: "",
    deposit_amount: "",
    amenities: [],
    available_date: "",
    is_available: true,
    images: [],
    // Contact/Location info
    contact_phone: salonInfo?.phone || "",
    contact_email: salonInfo?.email || "",
    address: salonInfo?.address || "",
    city: salonInfo?.city || "",
    state: salonInfo?.state || "",
    zip_code: salonInfo?.zip_code || ""
  });

  useEffect(() => {
    fetchListings();
  }, [salonId]);

  const fetchListings = async () => {
    if (!salonId) return;
    try {
      const response = await axios.get(`${API}/booths/salon/${salonId}/listings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(response.data.listings || []);
    } catch (error) {
      console.error("Failed to fetch listings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_type", "booth_gallery");

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      if (response.data.url) {
        setForm(prev => ({
          ...prev,
          images: [...prev.images, { 
            image_url: response.data.url, 
            caption: "", 
            is_primary: prev.images.length === 0 
          }]
        }));
        toast.success("Image uploaded!");
      }
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const setPrimaryImage = (index) => {
    setForm(prev => ({
      ...prev,
      images: prev.images.map((img, i) => ({
        ...img,
        is_primary: i === index
      }))
    }));
  };

  const toggleAmenity = (amenityId) => {
    setForm(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter(a => a !== amenityId)
        : [...prev.amenities, amenityId]
    }));
  };

  const saveListing = async () => {
    if (!form.name || !form.weekly_rate) {
      toast.error("Please fill in booth name and weekly rate");
      return;
    }

    try {
      const payload = {
        ...form,
        weekly_rate: parseFloat(form.weekly_rate) || 0,
        monthly_rate: form.monthly_rate ? parseFloat(form.monthly_rate) : null,
        deposit_amount: form.deposit_amount ? parseFloat(form.deposit_amount) : null,
        size_sqft: form.size_sqft ? parseInt(form.size_sqft) : null
      };

      if (editingListing) {
        await axios.put(`${API}/booths/listing/${editingListing.listing_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Listing updated!");
      } else {
        await axios.post(`${API}/booths/salon/${salonId}/listing`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Listing created!");
      }

      setShowCreateDialog(false);
      setEditingListing(null);
      resetForm();
      fetchListings();
    } catch (error) {
      toast.error("Failed to save listing");
    }
  };

  const deleteListing = async (listingId) => {
    if (!confirm("Delete this booth listing?")) return;
    try {
      await axios.delete(`${API}/booths/listing/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Listing deleted");
      fetchListings();
    } catch (error) {
      toast.error("Failed to delete listing");
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      description: "",
      booth_type: "standard",
      size_sqft: "",
      weekly_rate: "",
      monthly_rate: "",
      deposit_amount: "",
      amenities: [],
      available_date: "",
      is_available: true,
      images: [],
      contact_phone: salonInfo?.phone || "",
      contact_email: salonInfo?.email || "",
      address: salonInfo?.address || "",
      city: salonInfo?.city || "",
      state: salonInfo?.state || "",
      zip_code: salonInfo?.zip_code || ""
    });
  };

  const startEdit = (listing) => {
    setEditingListing(listing);
    setForm({
      name: listing.name || "",
      description: listing.description || "",
      booth_type: listing.booth_type || "standard",
      size_sqft: listing.size_sqft?.toString() || "",
      weekly_rate: listing.weekly_rate?.toString() || "",
      monthly_rate: listing.monthly_rate?.toString() || "",
      deposit_amount: listing.deposit_amount?.toString() || "",
      amenities: listing.amenities || [],
      available_date: listing.available_date || "",
      is_available: listing.is_available ?? true,
      images: listing.images || [],
      contact_phone: listing.contact_phone || salonInfo?.phone || "",
      contact_email: listing.contact_email || salonInfo?.email || "",
      address: listing.address || salonInfo?.address || "",
      city: listing.city || salonInfo?.city || "",
      state: listing.state || salonInfo?.state || "",
      zip_code: listing.zip_code || salonInfo?.zip_code || ""
    });
    setShowCreateDialog(true);
  };

  const downloadMarketingCard = async (listing) => {
    setSelectedListing(listing);
    // Wait for render
    setTimeout(async () => {
      if (cardRef.current) {
        try {
          const canvas = await html2canvas(cardRef.current, {
            backgroundColor: "#1c1917",
            scale: 2
          });
          const link = document.createElement("a");
          link.download = `booth-${listing.name.replace(/\s+/g, "-").toLowerCase()}.png`;
          link.href = canvas.toDataURL("image/png");
          link.click();
          toast.success("Marketing card downloaded!");
        } catch (error) {
          toast.error("Failed to generate card");
        }
      }
    }, 500);
  };

  const copyShareLink = (listing) => {
    const url = `${window.location.origin}/booth/${listing.listing_id}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  const getPrimaryImage = (listing) => {
    const primary = listing.images?.find(img => img.is_primary);
    return primary?.image_url || listing.images?.[0]?.image_url || null;
  };

  if (loading) {
    return (
      <Card className="bg-stone-800/50 border-stone-700">
        <CardContent className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-500" />
          <p className="text-stone-400 mt-2">Loading booth listings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Image className="w-5 h-5 text-amber-500" />
                Booth Marketing Gallery
              </CardTitle>
              <p className="text-stone-400 text-sm mt-1">
                Create booth listings with photos for marketing cards and shareable links
              </p>
            </div>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                  onClick={() => { resetForm(); setEditingListing(null); }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Booth Listing
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-stone-800 border-stone-700 max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white">
                    {editingListing ? "Edit Booth Listing" : "Create Booth Listing"}
                  </DialogTitle>
                </DialogHeader>
                
                <Tabs defaultValue="details" className="mt-4">
                  <TabsList className="bg-stone-700">
                    <TabsTrigger value="details">Details</TabsTrigger>
                    <TabsTrigger value="images">Images ({form.images.length})</TabsTrigger>
                    <TabsTrigger value="amenities">Amenities</TabsTrigger>
                    <TabsTrigger value="contact">Contact Info</TabsTrigger>
                  </TabsList>

                  <TabsContent value="details" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-stone-300">Booth Name *</Label>
                        <Input
                          value={form.name}
                          onChange={(e) => setForm({...form, name: e.target.value})}
                          placeholder="e.g., Corner Suite A"
                          className="bg-stone-700 border-stone-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-stone-300">Booth Type</Label>
                        <select
                          value={form.booth_type}
                          onChange={(e) => setForm({...form, booth_type: e.target.value})}
                          className="w-full p-2 bg-stone-700 border border-stone-600 rounded-md text-white"
                        >
                          <option value="standard">Standard Booth</option>
                          <option value="private_suite">Private Suite</option>
                          <option value="shared_space">Shared Space</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label className="text-stone-300">Description</Label>
                      <Textarea
                        value={form.description}
                        onChange={(e) => setForm({...form, description: e.target.value})}
                        placeholder="Describe this booth space..."
                        className="bg-stone-700 border-stone-600 text-white"
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-stone-300">Weekly Rate *</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                          <Input
                            type="number"
                            value={form.weekly_rate}
                            onChange={(e) => setForm({...form, weekly_rate: e.target.value})}
                            placeholder="150"
                            className="bg-stone-700 border-stone-600 text-white pl-8"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-stone-300">Monthly Rate</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                          <Input
                            type="number"
                            value={form.monthly_rate}
                            onChange={(e) => setForm({...form, monthly_rate: e.target.value})}
                            placeholder="500"
                            className="bg-stone-700 border-stone-600 text-white pl-8"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-stone-300">Deposit</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                          <Input
                            type="number"
                            value={form.deposit_amount}
                            onChange={(e) => setForm({...form, deposit_amount: e.target.value})}
                            placeholder="200"
                            className="bg-stone-700 border-stone-600 text-white pl-8"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-stone-300">Size (sq ft)</Label>
                        <Input
                          type="number"
                          value={form.size_sqft}
                          onChange={(e) => setForm({...form, size_sqft: e.target.value})}
                          placeholder="100"
                          className="bg-stone-700 border-stone-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-stone-300">Available From</Label>
                        <Input
                          type="date"
                          value={form.available_date}
                          onChange={(e) => setForm({...form, available_date: e.target.value})}
                          className="bg-stone-700 border-stone-600 text-white"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-stone-700/50 rounded-lg">
                      <div>
                        <p className="text-white font-medium">Available for Rent</p>
                        <p className="text-stone-400 text-sm">Show this listing publicly</p>
                      </div>
                      <Switch
                        checked={form.is_available}
                        onCheckedChange={(checked) => setForm({...form, is_available: checked})}
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="images" className="space-y-4 mt-4">
                    <div className="grid grid-cols-3 gap-4">
                      {form.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={img.image_url}
                            alt={img.caption || `Booth ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          {img.is_primary && (
                            <Badge className="absolute top-2 left-2 bg-amber-500 text-black">
                              Primary
                            </Badge>
                          )}
                          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!img.is_primary && (
                              <Button
                                size="sm"
                                variant="secondary"
                                className="h-7 w-7 p-0"
                                onClick={() => setPrimaryImage(idx)}
                              >
                                <Check className="w-3 h-3" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              className="h-7 w-7 p-0"
                              onClick={() => removeImage(idx)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      
                      {/* Upload button */}
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="h-32 border-2 border-dashed border-stone-600 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-amber-500 transition-colors"
                      >
                        {uploading ? (
                          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                        ) : (
                          <>
                            <Camera className="w-8 h-8 text-stone-500" />
                            <p className="text-stone-500 text-sm mt-2">Add Photo</p>
                          </>
                        )}
                      </div>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <p className="text-stone-500 text-xs text-center">
                      First image or image marked as primary will be used for marketing cards
                    </p>
                  </TabsContent>

                  <TabsContent value="amenities" className="mt-4">
                    <div className="grid grid-cols-2 gap-3">
                      {STANDARD_AMENITIES.map((amenity) => {
                        const Icon = AMENITY_ICONS[amenity.id] || Check;
                        const isSelected = form.amenities.includes(amenity.id);
                        return (
                          <div
                            key={amenity.id}
                            onClick={() => toggleAmenity(amenity.id)}
                            className={`p-3 rounded-lg border cursor-pointer transition-all flex items-center gap-3 ${
                              isSelected
                                ? "border-amber-500 bg-amber-500/10"
                                : "border-stone-600 hover:border-stone-500"
                            }`}
                          >
                            <div className={`w-8 h-8 rounded flex items-center justify-center ${
                              isSelected ? "bg-amber-500 text-black" : "bg-stone-700 text-stone-400"
                            }`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <span className={isSelected ? "text-white" : "text-stone-300"}>
                              {amenity.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </TabsContent>

                  <TabsContent value="contact" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-stone-300">Contact Phone</Label>
                        <Input
                          value={form.contact_phone}
                          onChange={(e) => setForm({...form, contact_phone: e.target.value})}
                          placeholder="(555) 123-4567"
                          className="bg-stone-700 border-stone-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-stone-300">Contact Email</Label>
                        <Input
                          type="email"
                          value={form.contact_email}
                          onChange={(e) => setForm({...form, contact_email: e.target.value})}
                          placeholder="info@salon.com"
                          className="bg-stone-700 border-stone-600 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-stone-300">Address</Label>
                      <Input
                        value={form.address}
                        onChange={(e) => setForm({...form, address: e.target.value})}
                        placeholder="123 Main St"
                        className="bg-stone-700 border-stone-600 text-white"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-stone-300">City</Label>
                        <Input
                          value={form.city}
                          onChange={(e) => setForm({...form, city: e.target.value})}
                          placeholder="Houston"
                          className="bg-stone-700 border-stone-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-stone-300">State</Label>
                        <Input
                          value={form.state}
                          onChange={(e) => setForm({...form, state: e.target.value})}
                          placeholder="TX"
                          className="bg-stone-700 border-stone-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-stone-300">ZIP Code</Label>
                        <Input
                          value={form.zip_code}
                          onChange={(e) => setForm({...form, zip_code: e.target.value})}
                          placeholder="77001"
                          className="bg-stone-700 border-stone-600 text-white"
                        />
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-stone-600">
                    Cancel
                  </Button>
                  <Button onClick={saveListing} className="bg-amber-500 hover:bg-amber-600 text-black">
                    {editingListing ? "Update Listing" : "Create Listing"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
      </Card>

      {/* Listings Grid */}
      {listings.length === 0 ? (
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="py-12 text-center">
            <Image className="w-12 h-12 mx-auto text-stone-600 mb-4" />
            <h3 className="text-white font-medium mb-2">No Booth Listings Yet</h3>
            <p className="text-stone-400 text-sm mb-4">
              Create booth listings with photos to generate marketing cards and shareable links
            </p>
            <Button onClick={() => setShowCreateDialog(true)} className="bg-amber-500 hover:bg-amber-600 text-black">
              <Plus className="w-4 h-4 mr-2" />
              Create First Listing
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((listing) => (
            <Card key={listing.listing_id} className="bg-stone-800/50 border-stone-700 overflow-hidden">
              {/* Image */}
              <div className="relative h-48 bg-stone-900">
                {getPrimaryImage(listing) ? (
                  <img
                    src={getPrimaryImage(listing)}
                    alt={listing.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-12 h-12 text-stone-700" />
                  </div>
                )}
                <Badge className={`absolute top-3 right-3 ${listing.is_available ? "bg-green-500" : "bg-red-500"}`}>
                  {listing.is_available ? "Available" : "Occupied"}
                </Badge>
              </div>

              <CardContent className="p-4">
                <h3 className="text-white font-semibold text-lg">{listing.name}</h3>
                <p className="text-amber-400 font-bold mt-1">
                  ${listing.weekly_rate}/week
                  {listing.monthly_rate && <span className="text-stone-400 font-normal"> • ${listing.monthly_rate}/mo</span>}
                </p>
                
                {listing.description && (
                  <p className="text-stone-400 text-sm mt-2 line-clamp-2">{listing.description}</p>
                )}

                {/* Amenities */}
                {listing.amenities?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {listing.amenities.slice(0, 4).map((amenityId) => {
                      const amenity = STANDARD_AMENITIES.find(a => a.id === amenityId);
                      return amenity ? (
                        <Badge key={amenityId} variant="outline" className="text-xs border-stone-600 text-stone-400">
                          {amenity.name}
                        </Badge>
                      ) : null;
                    })}
                    {listing.amenities.length > 4 && (
                      <Badge variant="outline" className="text-xs border-stone-600 text-stone-400">
                        +{listing.amenities.length - 4}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" className="flex-1 border-stone-600" onClick={() => startEdit(listing)}>
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="border-stone-600" onClick={() => downloadMarketingCard(listing)}>
                    <Download className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" className="border-stone-600" onClick={() => copyShareLink(listing)}>
                    <Share2 className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => deleteListing(listing.listing_id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Hidden Marketing Card for Download */}
      {selectedListing && (
        <div className="fixed -left-[9999px]">
          <div
            ref={cardRef}
            className="w-[1080px] h-[1080px] bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 p-12 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center">
                <DoorClosed className="w-8 h-8 text-black" />
              </div>
              <div>
                <h1 className="text-amber-400 text-4xl font-bold">BOOTH FOR RENT</h1>
                <p className="text-stone-400 text-xl">{salonInfo?.name || "Premium Salon"}</p>
              </div>
            </div>

            {/* Main Image */}
            {getPrimaryImage(selectedListing) && (
              <div className="flex-1 rounded-2xl overflow-hidden mb-8">
                <img
                  src={getPrimaryImage(selectedListing)}
                  alt={selectedListing.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Details */}
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h2 className="text-white text-3xl font-bold mb-4">{selectedListing.name}</h2>
                <div className="text-amber-400 text-5xl font-bold mb-2">
                  ${selectedListing.weekly_rate}<span className="text-2xl">/week</span>
                </div>
                {selectedListing.monthly_rate && (
                  <p className="text-stone-400 text-xl">${selectedListing.monthly_rate}/month available</p>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-white text-xl">
                  <MapPin className="w-6 h-6 text-amber-500" />
                  {selectedListing.city}, {selectedListing.state}
                </div>
                {selectedListing.contact_phone && (
                  <div className="flex items-center gap-3 text-white text-xl">
                    <Phone className="w-6 h-6 text-amber-500" />
                    {selectedListing.contact_phone}
                  </div>
                )}
                {selectedListing.contact_email && (
                  <div className="flex items-center gap-3 text-white text-xl">
                    <Mail className="w-6 h-6 text-amber-500" />
                    {selectedListing.contact_email}
                  </div>
                )}
              </div>
            </div>

            {/* QR Code */}
            <div className="absolute bottom-12 right-12 bg-white p-4 rounded-xl">
              <QRCode value={`${window.location.origin}/booth/${selectedListing.listing_id}`} size={120} />
              <p className="text-center text-black text-sm mt-2 font-medium">Scan to Apply</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoothMarketingManager;
